/* eslint-disable react/jsx-no-bind */
import React, { useState } from 'react';
import { getDefaultMeetingNoLength } from '../config';
import { STORAGE_JOIN_DISPLAY_NAME, STORAGE_JOIN_MEETING_NO_HISTORY, STORAGE_REMEMBER_JOIN_NAME } from '../constants';
import { generateMeetingNo } from '../functions';
import logger from '../logger';
import { PortalMessages as M } from '../messages';
function readRememberFlag() {
    try {
        return window.localStorage.getItem(STORAGE_REMEMBER_JOIN_NAME) === '1';
    }
    catch {
        return false;
    }
}
function readSavedName() {
    try {
        return window.localStorage.getItem(STORAGE_JOIN_DISPLAY_NAME) || '';
    }
    catch {
        return '';
    }
}
function persistName(rememberName, displayName) {
    try {
        window.localStorage.setItem(STORAGE_REMEMBER_JOIN_NAME, rememberName ? '1' : '0');
        if (rememberName) {
            window.localStorage.setItem(STORAGE_JOIN_DISPLAY_NAME, displayName.trim());
        }
        else {
            window.localStorage.removeItem(STORAGE_JOIN_DISPLAY_NAME);
        }
    }
    catch (error) {
        logger.warn(`记住入会名称写入失败 | ${error?.message || error}`);
    }
}
function persistMeetingNoHistory(meetingNo) {
    try {
        const raw = window.localStorage.getItem(STORAGE_JOIN_MEETING_NO_HISTORY);
        const parsed = raw ? JSON.parse(raw) : [];
        const prev = Array.isArray(parsed)
            ? parsed.map(item => String(item).replace(/\D/g, '')).filter(Boolean)
            : [];
        const next = [meetingNo, ...prev.filter(item => item !== meetingNo)].slice(0, 20);
        window.localStorage.setItem(STORAGE_JOIN_MEETING_NO_HISTORY, JSON.stringify(next));
    }
    catch (error) {
        logger.warn(`会议号历史写入失败 | ${error?.message || error}`);
    }
}
/**
 * 创建会议表单：手机全屏、桌面弹层，布局与加入会议对齐。
 *
 * @param {IProps} props - Props.
 * @returns {React.ReactElement}
 */
export default function CreateMeetingForm({ busy, defaultDisplayName, error, isMobile, onClose, onCreate }) {
    const rememberInitial = readRememberFlag();
    const meetingNoLength = getDefaultMeetingNoLength();
    const [title, setTitle] = useState('');
    const [meetingNo, setMeetingNo] = useState('');
    const [displayName, setDisplayName] = useState(() => (rememberInitial ? readSavedName() : '') || defaultDisplayName || '');
    const [rememberName, setRememberName] = useState(rememberInitial);
    const [micOn, setMicOn] = useState(true);
    const [speakerOn, setSpeakerOn] = useState(true);
    const [videoOn, setVideoOn] = useState(false);
    const [localError, setLocalError] = useState('');
    const canSubmit = Boolean(title.trim()) && !busy;
    const onSubmit = (event) => {
        event.preventDefault();
        const meetingTitle = title.trim();
        if (!meetingTitle) {
            setLocalError(M.CREATE_REQUIRED);
            return;
        }
        const customMeetingNo = meetingNo.replace(/\D/g, '');
        if (customMeetingNo.length > 0 && customMeetingNo.length < meetingNoLength) {
            setLocalError(M.MEETING_NO_INCOMPLETE);
            return;
        }
        setLocalError('');
        const finalMeetingNo = customMeetingNo || generateMeetingNo(meetingNoLength);
        persistName(rememberName, displayName);
        persistMeetingNoHistory(finalMeetingNo);
        logger.info(`[${new Date().toISOString()}] 提交创建会议表单 | title=${meetingTitle}`
            + ` meetingNo=${finalMeetingNo} micOn=${micOn} speakerOn=${speakerOn} videoOn=${videoOn}`);
        onCreate({
            displayName: displayName.trim(),
            meetingNo: finalMeetingNo,
            startSilent: !speakerOn,
            startWithAudioMuted: !micOn,
            startWithVideoMuted: !videoOn,
            title: meetingTitle
        });
    };
    const form = (React.createElement("form", { className: isMobile ? 'portal-join-form' : 'portal-join-form is-desktop', onClick: event => event.stopPropagation(), onSubmit: onSubmit },
        React.createElement("header", { className: 'portal-join-header' },
            React.createElement("button", { className: 'portal-join-cancel', onClick: onClose, type: 'button' }, M.CANCEL),
            React.createElement("h1", null, M.CREATE_MEETING),
            React.createElement("span", null)),
        React.createElement("div", { className: 'portal-join-rows' },
            React.createElement("div", { className: 'portal-join-row' },
                React.createElement("span", { className: 'portal-join-label' }, M.CREATE_TITLE),
                React.createElement("div", { className: 'portal-join-control' },
                    React.createElement("input", { autoFocus: true, disabled: busy, onChange: event => {
                            setLocalError('');
                            setTitle(event.target.value);
                        }, placeholder: M.CREATE_TITLE_PLACEHOLDER, value: title }))),
            React.createElement("div", { className: 'portal-join-row' },
                React.createElement("span", { className: 'portal-join-label' }, M.CREATE_MEETING_NO),
                React.createElement("div", { className: 'portal-join-control' },
                    React.createElement("input", { disabled: busy, onChange: event => {
                            setLocalError('');
                            setMeetingNo(event.target.value.replace(/\D/g, '').slice(0, meetingNoLength));
                        }, placeholder: M.CREATE_MEETING_NO_PLACEHOLDER, value: meetingNo }))),
            React.createElement("div", { className: 'portal-join-row' },
                React.createElement("span", { className: 'portal-join-label' }, M.YOUR_NAME),
                React.createElement("div", { className: 'portal-join-control' },
                    React.createElement("input", { disabled: busy, onChange: event => setDisplayName(event.target.value), value: displayName }))),
            React.createElement("div", { className: 'portal-join-row' },
                React.createElement("span", { className: 'portal-join-label is-muted' }, M.REMEMBER_JOIN_NAME),
                React.createElement("button", { "aria-checked": rememberName, className: rememberName ? 'portal-join-check is-on' : 'portal-join-check', onClick: () => setRememberName(value => !value), role: 'checkbox', type: 'button' },
                    React.createElement("span", null))),
            React.createElement("div", { className: 'portal-join-row' },
                React.createElement("span", { className: 'portal-join-label' }, M.ENABLE_MIC),
                React.createElement("button", { "aria-checked": micOn, className: micOn ? 'portal-join-switch is-on' : 'portal-join-switch', onClick: () => setMicOn(value => !value), role: 'switch', type: 'button' })),
            React.createElement("div", { className: 'portal-join-row' },
                React.createElement("span", { className: 'portal-join-label' }, M.ENABLE_SPEAKER),
                React.createElement("button", { "aria-checked": speakerOn, className: speakerOn ? 'portal-join-switch is-on' : 'portal-join-switch', onClick: () => setSpeakerOn(value => !value), role: 'switch', type: 'button' })),
            React.createElement("div", { className: 'portal-join-row' },
                React.createElement("span", { className: 'portal-join-label' }, M.ENABLE_VIDEO),
                React.createElement("button", { "aria-checked": videoOn, className: videoOn ? 'portal-join-switch is-on' : 'portal-join-switch', onClick: () => setVideoOn(value => !value), role: 'switch', type: 'button' }))),
        (localError || error) ? (React.createElement("div", { className: 'portal-error inline' }, localError || error)) : null,
        React.createElement("button", { className: 'portal-join-submit', disabled: !canSubmit, type: 'submit' }, M.CONFIRM_CREATE)));
    if (isMobile) {
        return (React.createElement("div", { className: 'portal-join-page' }, form));
    }
    return (React.createElement("div", { className: 'portal-modal-backdrop portal-join-desktop-backdrop', onClick: onClose, role: 'presentation' }, form));
}
